/** @jsxImportSource @emotion/react */
import { skipToken } from '@reduxjs/toolkit/query/react'
import moment from 'moment'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

import CheckboxComponent from 'components/atoms/Checkbox'
import Toast from 'components/atoms/Toast'
import {
  formatRateAmount,
  primaryConfigOn,
  StoredChargeConfig,
  todayIso,
} from 'components/lab/payer/chargeRules/chargeRule'
import { HST_CODE_TIER_LABELS, HST_CODE_TIERS } from 'components/lab/payer/hstCodes/hstCodes'
import { sortByDateOfService } from 'helpers/dates'
import {
  useCreateOrderChargeMutation,
  useGetOrderChargeDocumentsQuery,
  useGetOrderHstTieredChargeQuery,
} from 'store/services/labOrder'
import { Order } from 'store/types'
import type { HstCodeTier } from 'ts-back-end/src/business/payer/dto/payer.dto'

// Mirrors the fallback default in billing-charge-creation.service.ts (getCodeForNight).
const DEFAULT_CPT_CODE = '94762'

interface ServiceLineRow {
  id: string
  night: number
  reportId: number
  selected: boolean
  dos: string
  cptCode: string
  modifier: string
}

interface IProps {
  show?: boolean
  onClose: () => void
  order: Order
}

const isHstPsg = (testType?: string) => testType === 'HST' || testType === 'PSG'

const dosDay = (dos?: string) => (dos ? String(dos).slice(0, 10) : '')

type TieredLine = {
  component: string
  cptCode: string
  startDos: string
  endDos: string
  billedRate: number | null
  expectedAllowed: number | null
}

// The primary configuration of the primary payer in force on the charge's
// first date of service: billing prices and sets up every line by that day.
// PSG reads HST's configuration here, as this dialog always has.
const primaryConfigFor = (order: Order, reportDos: string) => {
  const primaryPayer = order?.payers?.find((payer) => Number(payer.position) === 1)?.labPayer
  const testType = order?.testType === 'PSG' ? 'HST' : order?.testType

  return testType
    ? primaryConfigOn(
        primaryPayer?.charges as StoredChargeConfig[] | undefined,
        testType,
        reportDos,
      )
    : undefined
}

const getDefaultCptCode = (order: Order, reportDos: string): string =>
  primaryConfigFor(order, reportDos)?.cptCode?.trim() || DEFAULT_CPT_CODE

// Max number of service lines that may be billed, from the primary payer's HST configuration.
// Mirrors getHstUnitsBillable in billing-charge-creation.service.ts (HST only).
const getMaxBillableUnits = (order: Order, reportDos: string): number | undefined => {
  if (order?.testType !== 'HST') {
    return undefined
  }
  const units = primaryConfigFor(order, reportDos)?.hstUnitsBillable

  return Number.isInteger(units) ? (units as number) : undefined
}

// The dialog's first date of service, which prices the charge; today while there is none.
const firstDos = (rows: { dos: string }[]) =>
  rows
    .map((row) => row.dos)
    .filter(Boolean)
    .sort()[0] || todayIso()

// Falls back to the report's created-at date (queuedAt, shown as "Created At" in the
// report listing) when no date of service is available.
const getReportCreatedDos = (report: any): string =>
  report?.queuedAt ? moment(report.queuedAt).format('YYYY-MM-DD') : ''

// Dates of service for a report, one per billable service line.
// HST and PSG bill one service line per date of service; POX/COX have a single
// testing date. PSG report uploads currently accept only one date (the "add another
// date of service" control is HST-only in ReportUploadModal), so PSG expands to a
// single night today and follows this rule automatically if that ever changes.
const getReportDosList = (report: any, testType?: string): string[] => {
  const fallback = getReportCreatedDos(report)

  if (isHstPsg(testType)) {
    const dates: string[] = (report?.datesOfService || []).filter(Boolean)
    if (dates.length) {
      return dates.map((date) => moment(date).format('YYYY-MM-DD'))
    }
    return [report?.dateOfService ? moment(report.dateOfService).format('YYYY-MM-DD') : fallback]
  }

  const raw = report?.testingTimeStarted

  return [raw ? moment(raw).format('YYYY-MM-DD') : fallback]
}

const buildRows = (order: Order): ServiceLineRow[] => {
  // Earliest date of service first; the night number breaks ties.
  const nights = sortByDateOfService(
    (order?.reportRenderRequests || [])
      .filter(
        (rr: any) =>
          rr.type === 'REPORT_PDF' && !rr.before && !rr.canceledAt && !rr.disregarded && !rr.wiped,
      )
      .sort((a: any, b: any) => (a.night > b.night ? 1 : -1))
      .flatMap((report: any) =>
        getReportDosList(report, order.testType).map((dos, idx) => ({
          id: `${report.reportId}-${idx}`,
          night: report.night,
          reportId: Number(report.reportId),
          selected: false,
          dos,
          modifier: '',
        })),
      ),
    (row) => row.dos,
  )
  const reportDos = firstDos(nights)
  const cptCode = getDefaultCptCode(order, reportDos)
  const maxUnits = getMaxBillableUnits(order, reportDos)

  // Rows follow the actual dates of service — one per DOS across the order's reports
  // (a report with 3 dates gives 3 nights, a report with 1 gives 1). The payer's units
  // billable is only a cap; nights are never duplicated to reach it.
  // Pre-select up to that cap (or every line when the payer has no cap), earliest first.
  const limit = maxUnits ?? nights.length

  return nights.map((row, index) => ({ ...row, cptCode, selected: index < limit }))
}

export const CreateChargeDialog = ({ show, onClose, order }: IProps) => {
  const [rows, setRows] = useState<ServiceLineRow[]>([])
  const [submitDocuments, setSubmitDocuments] = useState(false)
  // Document keys the user unchecked; every available document is selected by default.
  const [excludedDocKeys, setExcludedDocKeys] = useState<string[]>([])
  // The tier billing review picked; null until the order's own tier arrives.
  const [hstTier, setHstTier] = useState<HstCodeTier | null>(null)
  const [createOrderCharge, { isLoading }] = useCreateOrderChargeMutation()
  const {
    data: tieredCharge,
    isFetching: tieredFetching,
    error: tieredError,
  } = useGetOrderHstTieredChargeQuery(
    show && order?.testType === 'HST'
      ? { orderId: Number(order.id), tier: hstTier ?? undefined }
      : skipToken,
    // Always fresh on open: a report edited since the last look changes the lines.
    { refetchOnMountOrArgChange: true },
  )
  // Until the order's own answer arrives, neither the nights nor the tiered lines are known.
  const tieredLoading = tieredFetching && !hstTier
  const tiered = Boolean(tieredCharge?.tiered)
  const tieredLines = (tieredCharge?.lines ?? []) as TieredLine[]
  const { data: documents, isFetching: documentsLoading } = useGetOrderChargeDocumentsQuery(
    show && submitDocuments ? { orderId: Number(order.id) } : skipToken,
  )
  const wasOpen = useRef(false)

  // Rebuild rows only when the modal transitions from closed to open, so user edits
  // are preserved across re-renders (e.g. when a failed submit re-renders this dialog
  // with a new `order` reference).
  useEffect(() => {
    if (show && !wasOpen.current) {
      setRows(buildRows(order))
      setSubmitDocuments(false)
      setExcludedDocKeys([])
      setHstTier(null)
    }
    wasOpen.current = !!show
  }, [show, order])

  const updateRow = (id: string, patch: Partial<ServiceLineRow>) =>
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  // Picking a night for a payer that bills one unit replaces the night picked before.
  const selectOnlyRow = (id: string) =>
    setRows((prev) => prev.map((row) => ({ ...row, selected: row.id === id })))

  const maxUnits = getMaxBillableUnits(order, firstDos(rows))
  // Exactly one night can be billed, so the nights are a radio group.
  const singleUnit = maxUnits === 1
  const selectedRows = rows.filter((row) => row.selected)
  const maxReached = maxUnits !== undefined && selectedRows.length >= maxUnits

  const selectedDocKeys = (documents || [])
    .map((doc) => doc.key)
    .filter((key) => !excludedDocKeys.includes(key))

  const toggleDocument = (key: string, checked: boolean) =>
    setExcludedDocKeys((prev) => (checked ? prev.filter((k) => k !== key) : [...prev, key]))

  const billedTier = hstTier ?? tieredCharge?.tier ?? null
  const canSubmit = tiered ? Boolean(billedTier) && tieredLines.length > 0 : selectedRows.length > 0

  const handleSubmit = async () => {
    if (!tiered && !selectedRows.length) {
      Toast({ type: 'error', label: 'Select at least one date of service for the charge.' })
      return
    }

    const withDocuments = submitDocuments && selectedDocKeys.length > 0

    try {
      await createOrderCharge({
        orderId: Number(order.id),
        submitDocuments: withDocuments,
        documentKeys: withDocuments ? selectedDocKeys : undefined,
        // A tiered HST charge sends the tier; the server works out its lines.
        ...(tiered
          ? { nights: [], hstTier: billedTier as HstCodeTier }
          : {
              nights: selectedRows.map((row) => ({
                night: row.night,
                dos: row.dos || undefined,
                cptCode: row.cptCode || undefined,
                modifier: row.modifier || undefined,
              })),
            }),
      }).unwrap()

      Toast({ type: 'success', label: 'Charge created.' })
      onClose()
    } catch (e: any) {
      const message =
        typeof e?.data?.message === 'string'
          ? e.data.message
          : e?.data?.message?.message || 'Failed to create charge.'
      Toast({ type: 'error', label: message })
    }
  }

  return (
    <Modal centered size="lg" show={!!show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>Create Charge</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {tieredError && (
          <div className="alert alert-danger py-1 px-2" css={{ fontSize: 13 }}>
            {(tieredError as any)?.data?.message || 'The tiered HST charge could not be built.'}
          </div>
        )}
        {tieredLoading && <div className="text-muted mb-2">Loading…</div>}
        {!tieredLoading && tiered && (
          <div className="mb-2">
            <div className="text-muted mb-2" css={{ fontSize: 14 }}>
              This payer bills the tiered HST codes: TC and PC lines for each night, priced by the
              configuration in force on the first night.
            </div>
            <div className="d-flex align-items-center mb-2">
              <label className="mb-0 mr-2 font-weight-bold" htmlFor="charge-hst-tier">
                Tier
              </label>
              <select
                id="charge-hst-tier"
                className="form-control form-control-sm"
                css={{ width: 220 }}
                value={billedTier ?? ''}
                onChange={(e) => setHstTier((e.target.value || null) as HstCodeTier | null)}
              >
                {!billedTier && <option value="">Select a tier</option>}
                {HST_CODE_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {HST_CODE_TIER_LABELS[tier]}
                    {tier === tieredCharge?.defaultTier ? ' (device default)' : ''}
                  </option>
                ))}
              </select>
              {tieredFetching && (
                <span className="text-muted ml-2" css={{ fontSize: 12 }}>
                  Updating…
                </span>
              )}
            </div>
            {tieredCharge?.blockedReason && (
              <div className="alert alert-warning py-1 px-2" css={{ fontSize: 13 }}>
                {tieredCharge.blockedReason}
              </div>
            )}
            {tieredLines.length > 0 && (
              <table className="table table-sm mb-0" css={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>DOS</th>
                    <th>CPT</th>
                    <th>Component</th>
                    <th>Charge</th>
                    <th>Contracted Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {tieredLines.map((line, index) => (
                    <tr key={`${line.cptCode}-${line.startDos}-${index}`}>
                      <td>
                        {dosDay(line.startDos)}
                        {dosDay(line.endDos) !== dosDay(line.startDos)
                          ? ` – ${dosDay(line.endDos)}`
                          : ''}
                      </td>
                      <td>{line.cptCode}</td>
                      <td>{line.component}</td>
                      <td>{formatRateAmount(line.billedRate)}</td>
                      <td>{formatRateAmount(line.expectedAllowed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {!tieredLoading && !tiered && (
          <>
            <div className="text-muted mb-2" css={{ fontSize: 14 }}>
              {singleUnit
                ? 'Select the date of service to include in the charge. This payer bills 1 unit.'
                : `Select the dates of service to include in the charge. At least one must be selected${
                    maxUnits !== undefined ? `, up to a maximum of ${maxUnits} for this payer` : ''
                  }.`}
            </div>
            {rows.length === 0 ? (
              <div className="font-italic text-muted">No reports available for this order.</div>
            ) : (
              <div className="d-flex flex-column" css={{ gap: 8 }}>
                <div
                  className="row flex-nowrap align-items-center px-2 text-muted"
                  css={{ fontSize: 12 }}
                >
                  <div css={{ width: 110 }}>Night</div>
                  <div css={{ flex: 1 }}>DOS</div>
                  <div css={{ flex: 1 }}>CPT Code</div>
                  <div css={{ flex: 1 }}>Modifier</div>
                </div>
                {rows.map((row) => {
                  const checkboxDisabled = !singleUnit && maxReached && !row.selected
                  // A radio's night text picks the night too.
                  const NightCell = singleUnit ? 'label' : 'div'

                  return (
                    <div
                      key={row.id}
                      className="row flex-nowrap align-items-center p-2"
                      css={{
                        border: '1px solid #ced4da',
                        borderRadius: 4,
                        margin: 0,
                        opacity: checkboxDisabled ? 0.6 : 1,
                      }}
                    >
                      <NightCell
                        className="d-flex align-items-center mb-0"
                        htmlFor={singleUnit ? `charge-night-${row.id}` : undefined}
                        css={{ width: 110, cursor: singleUnit ? 'pointer' : undefined }}
                      >
                        {singleUnit ? (
                          <div className="d-flex align-items-center mr-2" css={{ width: 24 }}>
                            <input
                              type="radio"
                              id={`charge-night-${row.id}`}
                              name="charge-night"
                              aria-label={`Night ${row.night}, ${row.dos || 'no date'}`}
                              checked={row.selected}
                              onChange={() => selectOnlyRow(row.id)}
                              css={{ height: 16, width: 16, margin: 0, cursor: 'pointer' }}
                            />
                          </div>
                        ) : (
                          <CheckboxComponent
                            parentClassName="custom-control d-flex align-items-center mr-2"
                            css={{ height: 16, width: 16 }}
                            value={row.selected}
                            disabled={checkboxDisabled}
                            handleChange={(e: SyntheticEvent) =>
                              updateRow(row.id, {
                                selected: (e.target as HTMLInputElement).checked,
                              })
                            }
                          />
                        )}
                        <div className="d-flex flex-column">
                          <span className="font-weight-bold">{row.night}</span>
                          <span className="text-muted" css={{ fontSize: 12, lineHeight: 1.1 }}>
                            Report {row.reportId}
                          </span>
                        </div>
                      </NightCell>
                      <div css={{ flex: 1, paddingRight: 8 }}>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={row.dos}
                          disabled={!row.selected}
                          onChange={(e) => updateRow(row.id, { dos: e.target.value })}
                        />
                      </div>
                      <div css={{ flex: 1, paddingRight: 8 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.cptCode}
                          disabled={!row.selected}
                          onChange={(e) => updateRow(row.id, { cptCode: e.target.value })}
                        />
                      </div>
                      <div css={{ flex: 1 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.modifier}
                          disabled={!row.selected}
                          onChange={(e) => updateRow(row.id, { modifier: e.target.value })}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {maxReached && !singleUnit && (
              <div className="mt-2" css={{ fontSize: 12, color: '#d39e00' }}>
                Maximum of {maxUnits} {maxUnits === 1 ? 'unit' : 'units'} can be billed for this
                payer. Deselect a night to choose a different one.
              </div>
            )}
          </>
        )}
        <div className="d-flex align-items-center mt-3">
          <CheckboxComponent
            parentClassName="custom-control d-flex align-items-center mr-2"
            css={{ height: 16, width: 16 }}
            value={submitDocuments}
            handleChange={(e: SyntheticEvent) =>
              setSubmitDocuments((e.target as HTMLInputElement).checked)
            }
          />
          <span>Submit order documents with claim</span>
        </div>
        <div className="text-muted mt-1" css={{ fontSize: 12 }}>
          Attaches the order's RX, Office Notes, Authorization and valid Reports to the charge. They
          are sent to Optum automatically once the claim is submitted.
        </div>
        {submitDocuments && (
          <div className="mt-2 p-2" css={{ border: '1px solid #ced4da', borderRadius: 4 }}>
            {documentsLoading ? (
              <div className="text-muted" css={{ fontSize: 13 }}>
                Loading documents…
              </div>
            ) : !documents?.length ? (
              <div className="font-italic text-muted" css={{ fontSize: 13 }}>
                No documents available for this order.
              </div>
            ) : (
              <div className="d-flex flex-column" css={{ gap: 6 }}>
                {documents.map((doc) => (
                  <div key={doc.key} className="d-flex align-items-center">
                    <CheckboxComponent
                      parentClassName="custom-control d-flex align-items-center mr-2"
                      css={{ height: 16, width: 16 }}
                      value={!excludedDocKeys.includes(doc.key)}
                      handleChange={(e: SyntheticEvent) =>
                        toggleDocument(doc.key, (e.target as HTMLInputElement).checked)
                      }
                    />
                    <span className="font-weight-bold mr-2" css={{ fontSize: 13 }}>
                      {doc.fileType}
                    </span>
                    <span className="text-muted" css={{ fontSize: 13, wordBreak: 'break-all' }}>
                      {doc.fileName}
                    </span>
                  </div>
                ))}
                {!selectedDocKeys.length && (
                  <div css={{ fontSize: 12, color: '#d39e00' }}>
                    No documents selected — the charge will be created without attachments.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            isLoading || tieredFetching || !canSubmit || (submitDocuments && documentsLoading)
          }
          onClick={handleSubmit}
        >
          {isLoading ? 'Creating…' : 'Create Charge'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
